import React, { useState, useCallback } from 'react';
import { InjuryRiskTimelinePoint, SignalMetadataEntry } from '@/types';
import { celsiusToFahrenheit } from '@/utils/temperature';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SignalContributorCardProps {
  contribution: {
    signal: string;
    raw_value: number;
    risk_score: number;
    weighted_contribution: number;
    direction: string;
    explanation: string;
  };
  timelineData: InjuryRiskTimelinePoint[] | null;
  metadata: SignalMetadataEntry | undefined;
  isDark: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatValue(rawValue: number, unit: string): string {
  if (unit === 'ms') return rawValue > 0 ? rawValue.toFixed(0) : 'N/A';
  if (unit === 'bpm') return rawValue > 0 ? rawValue.toFixed(0) : 'N/A';
  if (unit === 'hrs') return rawValue.toFixed(1);
  if (unit === 'ratio') return rawValue.toFixed(2);
  if (unit === '/10') return rawValue > 0 ? rawValue.toFixed(0) : 'N/A';
  if (unit === '°C' || unit === 'C' || unit === 'degC') return celsiusToFahrenheit(rawValue).toFixed(1);
  return rawValue.toFixed(1);
}

/** Display unit string — convert °C to °F since formatValue handles the math. */
function displayUnit(unit: string): string {
  if (unit === '°C' || unit === 'C' || unit === 'degC') return '°F';
  return unit;
}

/**
 * Map derived signal names to their base timeline key.
 * Derived signals (slopes, z-scores, 3d-max, etc.) don't exist in the
 * timeline data — fall back to the raw signal they're computed from.
 */
const SIGNAL_TO_TIMELINE_KEY: Record<string, keyof InjuryRiskTimelinePoint> = {
  soreness_3d_max: 'soreness',
  soreness_slope: 'soreness',
  consecutive_high_soreness: 'soreness',
  hrv_slope: 'hrv_rmssd',
  hrv_7d_cv: 'hrv_rmssd',
  rhr_slope: 'rhr',
  rhr_zscore: 'rhr',
  sleep_zscore: 'sleep_score',
  mental_fatigue_slope: 'mental_fatigue',
  load_7d_sum: 'daily_load',
  load_volatility: 'daily_load',
  load_change_pct_7d: 'daily_load',
  session_load_spike_ratio: 'daily_load',
  acute_chronic_delta: 'acwr_composite',
  rpe_load_divergence: 'rpe',
  hrv_drop_x_load_spike: 'hrv_rmssd',
  regional_pain_3d_max: 'regional_pain_max',
  regional_asymmetry: 'regional_pain_max',
  region_consecutive_pain: 'regional_pain_max',
  injury_recurrence_risk: 'regional_pain_max',
};

/**
 * Extract the last N timeline values for a given signal key.
 * Falls back to the base signal for derived features.
 */
function extractSparklineValues(
  timelineData: InjuryRiskTimelinePoint[] | null,
  signal: string,
  count = 8,
): number[] {
  if (!timelineData || timelineData.length === 0) return [];
  const key = (SIGNAL_TO_TIMELINE_KEY[signal] || signal) as keyof InjuryRiskTimelinePoint;
  const all = timelineData
    .map(p => p[key] as number | null)
    .filter((v): v is number => v != null && typeof v === 'number' && !isNaN(v));
  return all.slice(-count);
}

type BarColor = { color: string; opacity: number };

function getBarColor(
  value: number,
  baseline: number,
  direction: string,
  dark: boolean,
): BarColor {
  const ratio = baseline !== 0 ? value / baseline : 1;
  const devRatio = baseline !== 0 ? Math.abs(value - baseline) / baseline : 0;
  const g = dark ? '#4ade80' : '#16a34a';
  const n = dark ? '#ffffff' : '#1a1a1a';
  const boost = dark ? 0 : 0.15;

  switch (direction) {
    case 'lower_is_worse':
      if (ratio > 1.05) return { color: g, opacity: 0.3 + boost };
      if (ratio > 0.9)  return { color: n, opacity: 0.15 + boost };
      if (ratio > 0.75) return { color: '#ef4444', opacity: 0.3 + boost };
      return { color: '#ef4444', opacity: 0.5 + boost };

    case 'higher_is_worse':
      if (ratio < 0.7)  return { color: g, opacity: 0.25 + boost };
      if (ratio < 0.9)  return { color: n, opacity: 0.15 + boost };
      if (ratio < 1.2)  return { color: '#ef4444', opacity: 0.3 + boost };
      return { color: '#ef4444', opacity: 0.5 + boost };

    case 'negative_is_worse': {
      // ACWR-style: absolute safe zone 0.8–1.3 (Gabbett 2016)
      if (value >= 0.8 && value <= 1.3) return { color: g, opacity: 0.25 + boost };
      if (value < 0.8) return { color: '#ef4444', opacity: 0.3 + boost };
      const excess = (value - 1.3) / 1.3;
      return { color: '#ef4444', opacity: Math.min(0.3 + excess * 0.5, 0.65) + boost };
    }

    case 'deviation_is_worse':
    default:
      if (devRatio < 0.05)  return { color: g, opacity: 0.3 + boost };
      if (devRatio < 0.15)  return { color: n, opacity: 0.15 + boost };
      if (devRatio < 0.3)   return { color: '#ef4444', opacity: 0.3 + boost };
      return { color: '#ef4444', opacity: 0.5 + boost };
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const SignalContributorCard: React.FC<SignalContributorCardProps> = ({
  contribution,
  timelineData,
  metadata,
  isDark,
}) => {
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const [hoveredX, setHoveredX] = useState<number | null>(null);

  const handleBarHover = useCallback((e: React.MouseEvent<HTMLDivElement>, barCount: number) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const idx = Math.floor((x / rect.width) * barCount);
    setHoveredIdx(Math.max(0, Math.min(idx, barCount - 1)));
    setHoveredX(x);
  }, []);

  const handleBarLeave = useCallback(() => {
    setHoveredIdx(null);
    setHoveredX(null);
  }, []);

  const { signal, raw_value, risk_score, weighted_contribution, explanation } = contribution;

  const label = metadata?.label ?? signal.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  const unit = metadata?.unit ?? '';
  const unitDisplay = displayUnit(unit);

  // Check if sparkline uses a fallback base signal (different from the derived signal)
  const baseSignalKey = SIGNAL_TO_TIMELINE_KEY[signal];
  const isUsingFallbackSignal = !!baseSignalKey;

  // For sparkline color logic: use the base signal's direction/baseline when falling back,
  // since the sparkline values are in the base signal's scale (e.g., HRV ms, not HRV slope)
  const BASE_SIGNAL_DEFAULTS: Record<string, { direction: string; baseline: number }> = {
    soreness: { direction: 'higher_is_worse', baseline: 4 },
    hrv_rmssd: { direction: 'lower_is_worse', baseline: 50 },
    rhr: { direction: 'higher_is_worse', baseline: 60 },
    sleep_score: { direction: 'lower_is_worse', baseline: 70 },
    mental_fatigue: { direction: 'higher_is_worse', baseline: 4 },
    daily_load: { direction: 'higher_is_worse', baseline: 500 },
    acwr_composite: { direction: 'negative_is_worse', baseline: 1.0 },
    rpe: { direction: 'higher_is_worse', baseline: 5 },
    regional_pain_max: { direction: 'higher_is_worse', baseline: 3 },
  };

  const sparkDefaults = isUsingFallbackSignal ? BASE_SIGNAL_DEFAULTS[baseSignalKey] : undefined;

  // metadata.direction uses 'lower_is_worse' / 'higher_is_worse' / 'deviation_is_worse'.
  // contribution.direction uses 'increasing_risk' / 'decreasing_risk' — map to bar color semantics.
  const direction = sparkDefaults?.direction
    ?? metadata?.direction
    ?? (contribution.direction === 'increasing_risk' ? 'higher_is_worse' : 'lower_is_worse');
  const hasRealBaseline = (sparkDefaults?.baseline != null) || (metadata?.baseline != null);
  const baseline = sparkDefaults?.baseline ?? metadata?.baseline ?? raw_value;

  const sparkValues = extractSparklineValues(timelineData, signal, 8);
  const maxVal = sparkValues.length > 0 ? Math.max(...sparkValues, 1) : 1;

  const points = Math.round(weighted_contribution * 100);

  // Border color based on risk_score — use card-border for non-risk state
  const borderColor =
    risk_score > 0.3
      ? `rgba(239,68,68,${Math.min(risk_score * 0.4, 0.3).toFixed(3)})`
      : 'var(--card-border)';

  // Trend prefix
  // Arrow based on risk direction, not severity magnitude
  const trendPrefix = contribution.direction === 'increasing_risk' ? '▲' : '▼';

  // Baseline as percentage of max for the dashed line overlay
  const baselinePct = maxVal > 0 ? Math.min((baseline / maxVal) * 100, 100) : 50;

  return (
    <div
      style={{
        background: 'var(--bg-card)',
        border: `1px solid ${borderColor}`,
        padding: '16px',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
        {/* Left: label + value */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <span style={{ fontSize: '10px', textTransform: 'uppercase', color: 'var(--text-secondary)', letterSpacing: '0.06em' }}>
            {label}
          </span>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '3px' }}>
            <span style={{ fontSize: '24px', fontWeight: 'bold', color: 'var(--text-primary)', lineHeight: 1 }}>
              {formatValue(raw_value, unit)}
            </span>
            {unitDisplay && (
              <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{unitDisplay}</span>
            )}
          </div>
        </div>

        {/* Right: SHAP badge */}
        {(() => {
          const isRisk = contribution.direction === 'increasing_risk';
          const badgeColor = isRisk ? '#ef4444' : (isDark ? '#4ade80' : '#16a34a');
          return (
            <div
              style={{
                fontSize: '10px',
                fontFamily: 'monospace',
                color: badgeColor,
                border: `1px solid ${isRisk ? 'rgba(239,68,68,0.25)' : (isDark ? 'rgba(74,222,128,0.25)' : 'rgba(22,163,74,0.25)')}`,
                background: isRisk ? 'rgba(239,68,68,0.06)' : (isDark ? 'rgba(74,222,128,0.06)' : 'rgba(22,163,74,0.06)'),
                padding: '2px 8px',
                whiteSpace: 'nowrap',
              }}
            >
              {isRisk ? '+' : '-'}{points}
            </div>
          );
        })()}
      </div>

      {/* Sparkline */}
      {sparkValues.length > 0 && (
        <div style={{ marginBottom: '4px', position: 'relative' }}>
          {/* Tooltip */}
          {hoveredIdx !== null && hoveredX !== null && sparkValues[hoveredIdx] !== undefined && (() => {
            const daysAgo = sparkValues.length - 1 - hoveredIdx;
            const dayLabel = daysAgo === 0 ? 'Today' : daysAgo === 1 ? '1 day ago' : `${daysAgo} days ago`;
            const isNearRight = hoveredIdx > sparkValues.length * 0.7;
            const isNearLeft = hoveredIdx < sparkValues.length * 0.3;
            return (
              <div
                style={{
                  position: 'absolute',
                  left: `${hoveredX}px`,
                  top: '-8px',
                  transform: `translateX(${isNearRight ? '-90%' : isNearLeft ? '-10%' : '-50%'}) translateY(-100%)`,
                  background: 'var(--bg-card)',
                  border: '1px solid var(--card-border)',
                  padding: '6px 10px',
                  fontSize: '10px',
                  whiteSpace: 'nowrap',
                  zIndex: 10,
                  pointerEvents: 'none',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  borderRadius: 0,
                }}
              >
                <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '2px' }}>
                  {dayLabel}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', color: 'var(--text-secondary)' }}>
                  <span>{label}:</span>
                  <span style={{ fontFamily: 'monospace', color: 'var(--text-primary)' }}>
                    {formatValue(sparkValues[hoveredIdx], unit)}{unitDisplay && ` ${unitDisplay}`}
                  </span>
                </div>
              </div>
            );
          })()}

          <div style={{ position: 'relative', height: '28px' }}>
            {/* Bars */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                gap: '1px',
                height: '28px',
                alignItems: 'flex-end',
                cursor: 'crosshair',
              }}
              onMouseMove={(e) => handleBarHover(e, sparkValues.length)}
              onMouseLeave={handleBarLeave}
            >
              {sparkValues.map((val, i) => {
                const heightPct = maxVal > 0 ? Math.max((val / maxVal) * 100, 4) : 4;
                const { color, opacity } = getBarColor(val, baseline, direction, isDark);
                const isHovered = i === hoveredIdx;
                const finalOpacity = isHovered ? Math.min(opacity + 0.2, 1) : opacity;
                return (
                  <div
                    key={i}
                    style={{
                      flex: 1,
                      height: `${heightPct}%`,
                      background: color,
                      opacity: finalOpacity,
                      transition: 'opacity 0.1s ease',
                    }}
                  />
                );
              })}
            </div>

            {/* Baseline dashed line overlay — only when real baseline available */}
            {hasRealBaseline && (
              <div
                style={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  bottom: `${baselinePct}%`,
                  height: '1px',
                  borderTop: `1px dotted ${isDark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.2)'}`,
                  pointerEvents: 'none',
                }}
              />
            )}

            {/* Vertical hover line */}
            {hoveredIdx !== null && hoveredX !== null && (
              <div
                style={{
                  position: 'absolute',
                  left: `${hoveredX}px`,
                  top: 0,
                  bottom: 0,
                  width: '1px',
                  borderLeft: '1px dashed var(--text-muted)',
                  opacity: 0.5,
                  pointerEvents: 'none',
                  zIndex: 5,
                }}
              />
            )}
          </div>

          {/* Meta row */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              fontSize: '9px',
              color: 'var(--text-muted)',
              marginTop: '3px',
            }}
          >
            <span>{sparkValues.length}d ago</span>
            {hasRealBaseline && (
              <span>
                baseline: {formatValue(baseline, unit)}
              </span>
            )}
            <span>today</span>
          </div>
        </div>
      )}

      {/* Explanation */}
      <p
        style={{
          fontSize: '11px',
          color: 'var(--text-secondary)',
          borderTop: '1px solid var(--card-border)',
          paddingTop: '10px',
          margin: '10px 0 0 0',
          lineHeight: 1.4,
        }}
      >
        <span style={{ marginRight: '4px', color: 'var(--text-secondary)' }}>{trendPrefix}</span>
        {explanation}
      </p>
    </div>
  );
};

export default SignalContributorCard;
